import React from 'react';
import { TextField, Switch, InputAdornment } from '@mui/material';
import InputMask from 'react-input-mask';
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import PopupBase from '../../../popup/base/PopupBase';
import CustomButton from '../../../components/buttons/CustomButton';
import styles from './ProcedureFormPopup.module.css';

/** "o nome, o preço e a duração": junta o que falta com vírgulas e "e" no fim. */
const joinMissing = (items) => (items.length > 1
    ? `${items.slice(0, -1).join(', ')} e ${items[items.length - 1]}`
    : items[0] || '');

/**
 * Criar ou editar um procedimento. O estado do formulário e a validação ficam
 * na página: aqui só a apresentação, em três blocos (identificação, valor e
 * duração, agendamento público).
 */
function ProcedureFormPopup({ mode, formData, isFormValid, missing = [], onInputChange, onFieldChange, onSubmit, onClose }) {
    const isCreate = mode === 'create';

    const content = (
        <div className={styles.content}>
            <section className={styles.block}>
                <span className={styles.blockTitle}>Identificação</span>
                <TextField
                    label="Nome do procedimento"
                    name="label"
                    value={formData.label}
                    onChange={onInputChange}
                    fullWidth
                    required
                    className={styles.input}
                />
                <TextField
                    label="Descrição"
                    name="description"
                    value={formData.description}
                    onChange={onInputChange}
                    fullWidth
                    multiline
                    rows={3}
                    placeholder="Descreva o procedimento"
                    className={styles.input}
                />
            </section>

            <section className={styles.block}>
                <span className={styles.blockTitle}>Valor e duração</span>
                <div className={styles.row}>
                    <TextField
                        label="Preço"
                        name="price"
                        value={formData.price}
                        onChange={onInputChange}
                        fullWidth
                        required
                        inputProps={{ inputMode: 'decimal' }}
                        placeholder="0,00"
                        InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }}
                        className={styles.input}
                    />
                    <InputMask
                        mask="99:99:00"
                        value={formData.duration}
                        onChange={onInputChange}
                        maskChar="0"
                    >
                        {() => (
                            <TextField
                                label="Duração"
                                name="duration"
                                fullWidth
                                required
                                helperText="Ex.: 00:50 para 50 minutos"
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <AccessTimeRoundedIcon fontSize="small" />
                                        </InputAdornment>
                                    )
                                }}
                                className={styles.input}
                            />
                        )}
                    </InputMask>
                </div>
            </section>

            <section className={styles.block}>
                <span className={styles.blockTitle}>Agendamento público</span>
                <div className={`${styles.toggleCard} ${formData.public_schedule_enabled ? styles.toggleCardOn : ''}`}>
                    <label className={styles.toggle}>
                        <span className={styles.toggleIcon}><PublicOutlinedIcon fontSize="small" /></span>
                        <span className={styles.toggleText}>
                            <span className={styles.toggleTitle}>Habilitar este serviço no agendamento público</span>
                            <span className={styles.toggleHint}>Aparece na página em que as pessoas marcam sozinhas.</span>
                        </span>
                        <Switch
                            checked={formData.public_schedule_enabled}
                            onChange={(e) => onFieldChange('public_schedule_enabled', e.target.checked)}
                            color="primary"
                            inputProps={{ 'aria-label': 'Habilitar no agendamento público' }}
                        />
                    </label>
                    {formData.public_schedule_enabled && (
                        <div className={styles.publicFields}>
                            <TextField
                                label="Nome público"
                                value={formData.public_schedule_name}
                                onChange={(e) => onFieldChange('public_schedule_name', e.target.value)}
                                fullWidth
                                size="small"
                                placeholder="Ex: Fisioterapia RPG"
                                helperText="Nome exibido na página de agendamento"
                                className={styles.input}
                            />
                            <TextField
                                label="Descrição pública"
                                value={formData.public_schedule_description}
                                onChange={(e) => onFieldChange('public_schedule_description', e.target.value)}
                                fullWidth
                                size="small"
                                multiline
                                rows={2}
                                placeholder="Ex: Sessão de fisioterapia com técnica RPG"
                                className={styles.input}
                            />
                        </div>
                    )}
                </div>
            </section>
        </div>
    );

    return (
        <PopupBase
            Title={isCreate ? 'Novo procedimento' : 'Editar procedimento'}
            Content={content}
            maxWidth="sm"
            Footer={
                <div className={styles.footer}>
                    {/* O que ainda falta para o botão ligar, como no Financeiro */}
                    <span className={styles.footerHint}>
                        {isFormValid ? '' : `Falta informar ${joinMissing(missing)}.`}
                    </span>
                    <CustomButton
                        style="primary"
                        variant="text"
                        label="Cancelar"
                        onClick={onClose}
                    />
                    <CustomButton
                        style="main"
                        variant="contained"
                        label={isCreate ? 'Adicionar' : 'Salvar'}
                        onClick={onSubmit}
                        disabled={!isFormValid}
                    />
                </div>
            }
            OnClose={onClose}
        />
    );
}

export default ProcedureFormPopup;
